import axios, { type AxiosRequestConfig } from 'axios';

let _currentTenantId: number | null = null;
let _getAccessToken: (() => Promise<string>) | null = null;
let _forceRefreshToken: (() => Promise<string>) | null = null;
let _logoutHandler: (() => void) | null = null;

export function setCurrentTenantId(tenantId: number | null) {
  _currentTenantId = tenantId;
}

export function setAccessTokenGetter(getter: () => Promise<string>) {
  _getAccessToken = getter;
}

export function setForceRefreshTokenGetter(getter: () => Promise<string>) {
  _forceRefreshToken = getter;
}

export function setLogoutHandler(handler: () => void) {
  _logoutHandler = handler;
}

// Break the "reload with same stale Auth0 state" loop. If auth failure recurs
// faster than this window, bypass the SDK and hard-wipe state before reloading.
const AUTH_FAIL_WINDOW_MS = 10_000;
const AUTH_FAIL_THRESHOLD = 3;
const AUTH_FAIL_KEY = 'letchat_auth_fail_log';

function recordAuthFailure(): boolean {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(AUTH_FAIL_KEY);
    const stamps: number[] = raw ? JSON.parse(raw) : [];
    const recent = stamps.filter((t) => now - t < AUTH_FAIL_WINDOW_MS);
    recent.push(now);
    sessionStorage.setItem(AUTH_FAIL_KEY, JSON.stringify(recent));
    return recent.length >= AUTH_FAIL_THRESHOLD;
  } catch {
    return false;
  }
}

function hardWipeAndReload() {
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('@@auth0spajs@@') || k === 'letchat_tenant_id') {
        localStorage.removeItem(k);
      }
    });
    sessionStorage.removeItem(AUTH_FAIL_KEY);
  } catch {
    // Storage disabled — reload anyway, the app will redirect to login.
  }
  globalThis.location.replace('/');
}

function bailToLogin() {
  if (recordAuthFailure() || !_logoutHandler) {
    hardWipeAndReload();
    return;
  }
  try {
    _logoutHandler();
  } catch {
    hardWipeAndReload();
  }
}

const TENANT_HEADER_WHITELIST = [
  '/user/profile',
  '/user/tenants',
  '/whatsapp/signup',
  '/notifications',
];

const isInvitationAuthRoute = (url: string) =>
  /^\/invitations\/[^/]+/.test(url) && !url.includes('/v1/');

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  // Without a timeout, a half-open connection / stalled proxy leaves the request
  // promise pending forever — e.g. an optimistic chat send would stay 'sending'
  // indefinitely because its mutation never settles into onError. 30s lets a hung
  // request reject so the optimistic message transitions to 'failed'. [FE-REL-02]
  timeout: 30_000,
  headers: {
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use(async (config) => {
  if (_getAccessToken) {
    try {
      const token = await _getAccessToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // Token retrieval failed; request proceeds without auth header
    }
  }

  const url = config.url ?? '';
  const isWhitelisted =
    TENANT_HEADER_WHITELIST.some((path) => url.includes(path)) ||
    isInvitationAuthRoute(url);

  if (!isWhitelisted && !config.headers['X-Tenant-ID'] && _currentTenantId) {
    config.headers['X-Tenant-ID'] = String(_currentTenantId);
  }

  return config;
});

// --- 401 refresh-retry queue ---

type QueueEntry = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: QueueEntry[] = [];

function drainQueue(error: unknown, token: string | null) {
  for (const entry of failedQueue) {
    if (token) {
      entry.resolve(token);
    } else {
      entry.reject(error);
    }
  }
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || !_forceRefreshToken || originalRequest._retry) {
      if (error.response?.status === 401) {
        bailToLogin();
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${token}` };
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await _forceRefreshToken();
      drainQueue(null, newToken);
      originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
      return apiClient(originalRequest);
    } catch (refreshError) {
      drainQueue(refreshError, null);
      bailToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
